import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import OwnerPageLayout from './layout/OwnerPageLayout';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { postWithCrossSchoolLink } from '../utils/crossSchoolDuplicate';
import DemoViewBanner from '../demo/DemoViewBanner';

import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';


const API = getApiBaseUrl();

export default function StaffManager() {
    const { t } = useTranslation(['staffManager', 'common', 'nav', 'messages', 'errors', 'students']);
    const roleLabel = (role) => {
        if (role === 'assistant') return t('staffManager:roleAssistantShort');
        if (role === 'tech') return t('staffManager:roleTech');
        if (role === 'admin') return t('staffManager:roleAdmin');
        if (role === 'other') return t('staffManager:roleOther');
        return t('staffManager:roleCoach');
    };
    const [school, setSchool] = useState(null);
    const [staff, setStaff] = useState([]);
    const [groups, setGroups] = useState([]);
    const [events, setEvents] = useState([]);
    const [slots, setSlots] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState('');
    const [loading, setLoading] = useState(true);

    const [staffForm, setStaffForm] = useState({
        id: null, fullName: '', role: 'coach', phone: '', email: '', notes: '', linkUserId: ''
    });
    const [assignForm, setAssignForm] = useState({ groupId: '', eventId: '', slotId: '', roleInGroup: '', roleInEvent: '', roleInSlot: '' });

    const token = localStorage.getItem('token');
    const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

    useEffect(() => {
        let cancelled = false;
        loadData(() => cancelled);
        return () => { cancelled = true; };
    }, []);

    async function loadData(isCancelled = () => false) {
        setLoading(true);
        try {
            const schoolRes = await getSchoolForSession(api);
            const schoolId = getSchoolIdFromResponse(schoolRes);
            setSchool(schoolRes.data);

            const from = new Date();
            from.setMonth(from.getMonth() - 1);
            const to = new Date();
            to.setMonth(to.getMonth() + 6);

            const [staffRes, groupsRes, eventsRes, slotsRes] = await Promise.all([
                api.get('/staff', { params: { schoolId } }),
                api.get(`/school/${schoolId}/groups`),
                api.get('/event', { params: { schoolId } }),
                api.get('/schedule', {
                    params: {
                        schoolId,
                        from: from.toISOString().slice(0, 10),
                        to: to.toISOString().slice(0, 10)
                    }
                })
            ]);

            setStaff(staffRes.data || []);
            setGroups(groupsRes.data || []);
            setEvents(eventsRes.data || []);
            setSlots(slotsRes.data || []);
        } catch (e) {
            if (!isCancelled()) {
                alert(e.response?.status === 403
                    ? t('staffManager:errNoAccess')
                    : (e.response?.data?.message || t('staffManager:errLoad')));
            }
        }
        if (!isCancelled()) setLoading(false);
    }

    const selectedStaff = useMemo(
        () => staff.find(s => String(s.id) === String(selectedStaffId)) || null,
        [staff, selectedStaffId]
    );

    async function handleSaveStaff(e) {
        e.preventDefault();
        try {
            if (staffForm.id) {
                await api.put(`/staff/${staffForm.id}`, payloadStaff(staffForm));
            } else {
                const res = await postWithCrossSchoolLink(
                    api, '/staff', payloadStaff(staffForm), { schoolId: school?.id },
                    { linkParam: 'linkFromStaffId', idKey: 'staffId', entityLabel: t('staffManager:entityStaff') }
                );
                if (!res) return;
                if (res.data?.linkedFromOtherSchool) {
                    alert(t('staffManager:msgLinkedSchool'));
                }
            }
            setStaffForm({
                id: null, fullName: '', role: 'coach', phone: '', email: '', notes: '', linkUserId: ''
            });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errSave'));
        }
    }

    async function handleLinkUser() {
        if (!staffForm.id || !staffForm.linkUserId) return;
        try {
            await api.post(`/staff/${staffForm.id}/link-user`, {
                userId: parseInt(staffForm.linkUserId, 10)
            });
            alert(t('staffManager:msgAccountLinked'));
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errLinkAccount'));
        }
    }

    async function handleDeactivateStaff(id) {
        if (!window.confirm(t('staffManager:confirmDeactivate'))) return;
        try {
            await api.post(`/staff/${id}/deactivate`);
            if (String(id) === String(selectedStaffId)) setSelectedStaffId('');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errDeactivate'));
        }
    }

    async function assignGroup() {
        if (!selectedStaff || !assignForm.groupId) return;
        try {
            await api.post('/staff/assign/group', {
                staffId: selectedStaff.id,
                groupId: parseInt(assignForm.groupId, 10),
                roleInGroup: assignForm.roleInGroup || null
            });
            setAssignForm({ ...assignForm, groupId: '', roleInGroup: '' });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errAssignGroup'));
        }
    }

    async function assignEvent() {
        if (!selectedStaff || !assignForm.eventId) return;
        try {
            await api.post('/staff/assign/event', {
                staffId: selectedStaff.id,
                eventId: parseInt(assignForm.eventId, 10),
                roleInEvent: assignForm.roleInEvent || null
            });
            setAssignForm({ ...assignForm, eventId: '', roleInEvent: '' });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errAssignEvent'));
        }
    }

    async function assignSlot() {
        if (!selectedStaff || !assignForm.slotId) return;
        try {
            await api.post('/staff/assign/slot', {
                staffId: selectedStaff.id,
                slotId: parseInt(assignForm.slotId, 10),
                roleInSlot: assignForm.roleInSlot || null
            });
            setAssignForm({ ...assignForm, slotId: '', roleInSlot: '' });
            await loadData();
        } catch (err) {
            alert(err.response?.data?.message || t('staffManager:errAssignSlot'));
        }
    }

    async function unassignGroup(groupId) {
        if (!selectedStaff) return;
        await api.delete('/staff/assign/group', { params: { staffId: selectedStaff.id, groupId } });
        await loadData();
    }

    async function unassignEvent(eventId) {
        if (!selectedStaff) return;
        await api.delete('/staff/assign/event', { params: { staffId: selectedStaff.id, eventId } });
        await loadData();
    }

    async function unassignSlot(slotId) {
        if (!selectedStaff) return;
        await api.delete('/staff/assign/slot', { params: { staffId: selectedStaff.id, slotId } });
        await loadData();
    }

    if (loading) {
        return (
            <OwnerPageLayout title={t('staffManager:titleLoading')} maxWidth={1200} beforeMain={<DemoViewBanner />}>
                <div className="sp-loading-page">{t('staffManager:loading')}</div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('staffManager:title')}
            subtitle={t('staffManager:subtitle', { name: school?.name || '' })}
            maxWidth={1200}
            beforeMain={<DemoViewBanner />}
        >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '14px' }}>
                    <div className="sp-panel" style={card}>
                        <h3 style={{ marginTop: 0 }}>{t('staffManager:staffList')}</h3>
                        <select value={selectedStaffId} onChange={e => setSelectedStaffId(e.target.value)} style={input}>
                            <option value="">{t('staffManager:selectStaff')}</option>
                            {staff.map(s => <option key={s.id} value={s.id}>{s.fullName} · {s.role}{s.isActive ? '' : t('staffManager:inactiveSuffix')}</option>)}
                        </select>
                        <div style={{ display: 'grid', gap: '6px', marginTop: '10px' }}>
                            {staff.map(s => (
                                <div key={s.id} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{s.fullName}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                                            {roleLabel(s.role)} · {s.phone || t('staffManager:noPhone')}
                                            {s.userId ? t('staffManager:accountLinked', { id: s.userId }) : t('staffManager:noAccount')}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <button onClick={() => setStaffForm({ id: s.id, fullName: s.fullName || '', role: s.role || 'coach', phone: s.phone || '', email: s.email || '', notes: s.notes || '' })} style={ghostBtn}>{t('common:edit')}</button>
                                        {s.isActive && <button onClick={() => handleDeactivateStaff(s.id)} style={dangerBtn}>{t('staffManager:deactivate')}</button>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="sp-panel" style={card}>
                        <h3 className="sp-panel__title" style={{ marginTop: 0 }}>{staffForm.id ? t('staffManager:editStaff') : t('staffManager:newStaff')}</h3>
                        <form onSubmit={handleSaveStaff} style={{ display: 'grid', gap: '8px' }}>
                            <input required placeholder={t('staffManager:fullName')} value={staffForm.fullName} onChange={e => setStaffForm({ ...staffForm, fullName: e.target.value })} style={input} />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <select value={staffForm.role} onChange={e => setStaffForm({ ...staffForm, role: e.target.value })} style={input}>
                                    <option value="coach">{t('staffManager:roleCoach')}</option>
                                    <option value="assistant">{t('staffManager:roleAssistant')}</option>
                                    <option value="tech">{t('staffManager:roleTech')}</option>
                                    <option value="admin">{t('staffManager:roleAdmin')}</option>
                                    <option value="other">{t('staffManager:roleOther')}</option>
                                </select>
                                <input placeholder={t('students:phone')} value={staffForm.phone} onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })} style={input} />
                            </div>
                            <input placeholder={t('staffManager:emailHint')} value={staffForm.email} onChange={e => setStaffForm({ ...staffForm, email: e.target.value })} style={input} />
                            {staffForm.id && (
                                <input
                                    type="number"
                                    min="1"
                                    placeholder={t('staffManager:userIdPlaceholder')}
                                    value={staffForm.linkUserId}
                                    onChange={e => setStaffForm({ ...staffForm, linkUserId: e.target.value })}
                                    style={input}
                                />
                            )}
                            <input placeholder={t('staffManager:notes')} value={staffForm.notes} onChange={e => setStaffForm({ ...staffForm, notes: e.target.value })} style={input} />
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button type="submit" style={primaryBtn}>{staffForm.id ? t('common:save') : t('staffManager:addStaff')}</button>
                                {staffForm.id && staffForm.linkUserId && (
                                    <button type="button" onClick={handleLinkUser} style={ghostBtn}>{t('staffManager:linkAccount')}</button>
                                )}
                                {staffForm.id && (
                                    <button type="button" onClick={() => setStaffForm({
                                        id: null, fullName: '', role: 'coach', phone: '', email: '', notes: '', linkUserId: ''
                                    })} style={ghostBtn}>{t('staffManager:reset')}</button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>

                {selectedStaff && (
                    <div style={{ ...card, marginTop: '14px' }}>
                        <h3 style={{ marginTop: 0 }}>{t('staffManager:assignments', { name: selectedStaff.fullName })}</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('staffManager:toGroups')}</div>
                                <select value={assignForm.groupId} onChange={e => setAssignForm({ ...assignForm, groupId: e.target.value })} style={input}>
                                    <option value="">{t('staffManager:selectGroup')}</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </select>
                                <input placeholder={t('staffManager:roleInGroup')} value={assignForm.roleInGroup} onChange={e => setAssignForm({ ...assignForm, roleInGroup: e.target.value })} style={{ ...input, marginTop: '6px' }} />
                                <button onClick={assignGroup} style={{ ...primaryBtn, marginTop: '6px' }}>{t('staffManager:attach')}</button>
                                <div style={{ display: 'grid', gap: '4px', marginTop: '8px' }}>
                                    {(selectedStaff.groups || []).map(g => (
                                        <div key={g.groupId} style={assignRow}>
                                            <span>{g.groupName} {g.roleInGroup ? `(${g.roleInGroup})` : ''}</span>
                                            <button onClick={() => unassignGroup(g.groupId)} style={dangerBtn}>{t('staffManager:remove')}</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('staffManager:toEvents')}</div>
                                <select value={assignForm.eventId} onChange={e => setAssignForm({ ...assignForm, eventId: e.target.value })} style={input}>
                                    <option value="">{t('staffManager:selectEvent')}</option>
                                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                                </select>
                                <input placeholder={t('staffManager:roleInEvent')} value={assignForm.roleInEvent} onChange={e => setAssignForm({ ...assignForm, roleInEvent: e.target.value })} style={{ ...input, marginTop: '6px' }} />
                                <button onClick={assignEvent} style={{ ...primaryBtn, marginTop: '6px' }}>{t('staffManager:attach')}</button>
                                <div style={{ display: 'grid', gap: '4px', marginTop: '8px' }}>
                                    {(selectedStaff.events || []).map(ev => (
                                        <div key={ev.schoolEventId} style={assignRow}>
                                            <span>{ev.eventTitle} {ev.roleInEvent ? `(${ev.roleInEvent})` : ''}</span>
                                            <button onClick={() => unassignEvent(ev.schoolEventId)} style={dangerBtn}>{t('staffManager:remove')}</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px' }}>
                                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('staffManager:toSchedule')}</div>
                                <select value={assignForm.slotId} onChange={e => setAssignForm({ ...assignForm, slotId: e.target.value })} style={input}>
                                    <option value="">{t('staffManager:selectSlot')}</option>
                                    {slots.map(sl => <option key={sl.id} value={sl.id}>{sl.groupName} · {sl.startTime}-{sl.endTime}</option>)}
                                </select>
                                <input placeholder={t('staffManager:roleInSlot')} value={assignForm.roleInSlot} onChange={e => setAssignForm({ ...assignForm, roleInSlot: e.target.value })} style={{ ...input, marginTop: '6px' }} />
                                <button onClick={assignSlot} style={{ ...primaryBtn, marginTop: '6px' }}>{t('staffManager:attach')}</button>
                                <div style={{ display: 'grid', gap: '4px', marginTop: '8px' }}>
                                    {(selectedStaff.slots || []).map(sl => (
                                        <div key={sl.groupScheduleSlotId} style={assignRow}>
                                            <span>{sl.groupName} {sl.startTime}-{sl.endTime} {sl.roleInSlot ? `(${sl.roleInSlot})` : ''}</span>
                                            <button onClick={() => unassignSlot(sl.groupScheduleSlotId)} style={dangerBtn}>{t('staffManager:remove')}</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </OwnerPageLayout>
    );
}

function payloadStaff(form) {
    return {
        fullName: form.fullName,
        role: form.role,
        phone: form.phone || null,
        email: form.email || null,
        notes: form.notes || null
    };
}

const card = { background: '#fff', borderRadius: '12px', border: '1px solid var(--sp-gray-100)', padding: '14px' };
const input = { width: '100%', border: '1px solid #e5e7eb', borderRadius: '9px', padding: '9px 10px', fontSize: '14px', boxSizing: 'border-box' };
const primaryBtn = { border: 'none', borderRadius: '8px', padding: '8px 12px', background: 'var(--sp-primary)', color: 'white', cursor: 'pointer', fontSize: '13px' };
const dangerBtn = { border: 'none', borderRadius: '8px', padding: '6px 10px', background: '#b91c1c', color: 'white', cursor: 'pointer', fontSize: '12px' };
const ghostBtn = { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '8px 12px', background: '#fff', color: '#334155', cursor: 'pointer', fontSize: '13px' };
const assignRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '6px', fontSize: '12px' };
